import { convertMoneyToNumber } from './currency';
import { hashEmail, hashPhone } from './sha256';
import {
  isMoosendEnabled,
  isMoosendTrackingEnabled,
  trackMoosendEvent,
  trackMoosendPageView,
} from './moosendTracking';

const RETAIL_VERTICAL = 'retail';

// Helper to ensure IDs are strings (Sharetribe uses UUID objects)
const sanitizeId = id => {
  if (id && typeof id === 'object' && id.uuid) {
    return id.uuid;
  }
  return id;
};

const normalizeItemsForAnalytics = items => {
  if (!Array.isArray(items)) {
    return items;
  }

  return items.map(item => {
    const { item_image, item_url, itemImage, itemUrl, imageUrl, moosendItemImage, moosendItemUrl, ...rest } =
      item;

    const itemId = sanitizeId(item.item_id || item.id);
    return {
      ...rest,
      item_id: itemId, // GA4 standard
      id: itemId,      // Google Ads dynamic remarketing / product feed match
      quantity: typeof item.quantity === 'number' ? item.quantity : 1,
      google_business_vertical: item.google_business_vertical || RETAIL_VERTICAL,
    };
  });
};

// Calculate a default event value from the items array so value and items stay in sync.
const calculateValueFromItems = items => {
  if (!Array.isArray(items)) {
    return 0;
  }
  return items.reduce((sum, item) => {
    const price = typeof item.price === 'number' ? item.price : 0;
    const quantity = typeof item.quantity === 'number' ? item.quantity : 1;
    return sum + price * quantity;
  }, 0);
};

// Keep track of sent transaction IDs in the current session to prevent double-counting
const getSentTransactions = () => {
  try {
    return JSON.parse(sessionStorage.getItem('sent_transactions') || '[]');
  } catch (e) {
    return [];
  }
};

const markTransactionAsSent = id => {
  if (!id) return;
  try {
    const sent = getSentTransactions();
    if (!sent.includes(id)) {
      sent.push(id);
      sessionStorage.setItem('sent_transactions', JSON.stringify(sent));
    }
  } catch (e) {
    // Ignore storage failures so analytics dispatch still proceeds.
  }
};

const MOOSEND_PENDING_PURCHASE_KEY = 'moosendPendingPurchase';
const MOOSEND_PURCHASE_SENT_PREFIX = 'moosendPurchaseSent:';

const moosendPurchaseSentKey = transactionId => `${MOOSEND_PURCHASE_SENT_PREFIX}${transactionId}`;

export const storePendingMoosendPurchase = purchaseParams => {
  if (!isMoosendEnabled()) return;
  if (typeof window === 'undefined' || !purchaseParams) return;

  try {
    window.localStorage.setItem(
      MOOSEND_PENDING_PURCHASE_KEY,
      JSON.stringify({
        ...purchaseParams,
        createdAt: Date.now(),
      })
    );
  } catch (e) {
    // Ignore storage failures so checkout can continue.
  }
};

export const markMoosendPurchaseTracked = transactionId => {
  if (!isMoosendEnabled()) return;
  if (typeof window === 'undefined' || !transactionId) return;

  try {
    window.localStorage.setItem(moosendPurchaseSentKey(transactionId), 'true');
    window.localStorage.removeItem(MOOSEND_PENDING_PURCHASE_KEY);
  } catch (e) {
    // Ignore storage failures.
  }
};

export const trackPendingMoosendPurchase = (transactionId, fallbackParams = {}) => {
  if (!isMoosendEnabled()) return false;
  if (typeof window === 'undefined' || !transactionId) return false;

  try {
    if (window.localStorage.getItem(moosendPurchaseSentKey(transactionId))) return false;

    const pendingParams = JSON.parse(
      window.localStorage.getItem(MOOSEND_PENDING_PURCHASE_KEY) || 'null'
    );
    const purchaseParams = {
      ...pendingParams,
      ...fallbackParams,
      transaction_id: transactionId,
      items: fallbackParams.items || pendingParams?.items || [],
    };

    if (!purchaseParams.items.length) return false;

    const wasTracked = trackMoosendEvent('purchase', purchaseParams);
    if (wasTracked) {
      markMoosendPurchaseTracked(transactionId);
    }
    return wasTracked;
  } catch (e) {
    return false;
  }
};

// Transaction process bases that mean the order ships with authentication &
// quality control: pop nouvelle items (automatic) and orders where the buyer
// picked the authenticated shipping option.
const AUTHENTICATED_PROCESSES = ['from-popnouvelle-booking-process', 'advance-default-purchase'];

const getShippingTier = processName => {
  const processBase = typeof processName === 'string' ? processName.split('/')[0] : '';
  return AUTHENTICATED_PROCESSES.includes(processBase)
    ? 'Authenticated Shipping'
    : 'Direct Shipping';
};

// Payment method types returned by the Stripe API, mapped to the labels the
// marketing team expects in the purchase event.
const PAYMENT_TYPE_LABELS = {
  card: 'Credit Card',
  paypal: 'PayPal',
  klarna: 'Klarna',
};

/**
 * Build the additional purchase fields requested by the marketing company:
 * shipping_tier, shipping cost, payment_type, coupon and the SHA-256 hashed
 * customer email/phone, plus the Consent Mode flags. Call sites pass whatever
 * they know; missing values fall back to sensible defaults.
 */
export const getPurchaseEventExtras = ({
  processName,
  lineItems,
  promoCode,
  email,
  phone,
  paymentMethodType,
} = {}) => {
  const shippingLineItem = Array.isArray(lineItems)
    ? lineItems.find(li => li.code === 'line-item/shipping-fee')
    : null;
  // Analytics must never throw during checkout — fall back to 0 if the line
  // total is not a proper Money value.
  let shipping = 0;
  if (shippingLineItem?.lineTotal) {
    try {
      shipping = Math.abs(convertMoneyToNumber(shippingLineItem.lineTotal));
    } catch (e) {
      shipping = 0;
    }
  }
  const emailHash = hashEmail(email);
  const phoneHash = hashPhone(phone);

  const extras = {
    url_passthrough: false,
    ads_data_redaction: false,
    shipping_tier: getShippingTier(processName),
    shipping,
    // All payments in this flow go through Stripe; label by the method the
    // buyer picked in the embedded checkout (stored on the transaction).
    payment_type: PAYMENT_TYPE_LABELS[paymentMethodType] || 'Credit Card',
    coupon: promoCode || '',
  };
  if (emailHash) {
    extras.email = emailHash;
  }
  if (phoneHash) {
    extras.phone = phoneHash;
  }
  return extras;
};

/**
 * Trigger a standard analytics event across Moosend and the GTM dataLayer.
 *
 * Direct channel sends (GA4 gtag, Google Ads send_to, Facebook Pixel fbq) have
 * been removed; events are pushed to window.dataLayer so the marketing
 * company's GTM container can route them centrally and avoid duplicate tracking.
 *
 * @param {string} eventName - Standard GA4 event name (view_item, add_to_cart, etc.)
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  const isEnabled = process.env.REACT_APP_ENABLE_MARKETING_TRACKING === 'true';
  const isMoosendTrackingActive = isMoosendTrackingEnabled();
  if (!isEnabled && !isMoosendTrackingActive) return false;

  const { moosendItemImage, moosendItemUrl, ...analyticsParams } = params;
  const moosendParams = params;

  // Sanitize transaction_id if present
  const transactionId = sanitizeId(analyticsParams.transaction_id);

  // Deduplication for 'purchase' event
  if (eventName === 'purchase' && transactionId) {
    const sent = getSentTransactions();
    if (sent.includes(transactionId)) {
      console.log(`[Analytics] Skipping duplicate purchase event for ID: ${transactionId}`);
      return false;
    }
  }

  const normalizedItems = normalizeItemsForAnalytics(analyticsParams.items);
  const normalizedParams = {
    ...analyticsParams,
    transaction_id: transactionId,
    items: normalizedItems,
  };

  // Keep value in sync with the items array when no explicit value is supplied.
  // Call sites that pass an explicit value (e.g. purchase total including shipping)
  // keep full control.
  if (typeof normalizedParams.value !== 'number' && Array.isArray(normalizedItems)) {
    normalizedParams.value = calculateValueFromItems(normalizedItems);
  }

  // DataLayer (for GTM custom tags) — no send_to; GTM routes events centrally.
  if (isEnabled && window.dataLayer) {
    if (Array.isArray(normalizedItems)) {
      // Ecommerce events follow the GA4 dataLayer standard: clear any stale
      // ecommerce object first, then push only this event's payload under
      // 'ecommerce' so no data from a previous event lingers in the model.
      window.dataLayer.push({ ecommerce: null });
      if (eventName === 'purchase') {
        // Consent Mode flags belong at the top level of the push, next to the
        // event name — not inside the ecommerce object.
        const { url_passthrough, ads_data_redaction, ...ecommerceParams } = normalizedParams;
        window.dataLayer.push({
          event: eventName,
          url_passthrough,
          ads_data_redaction,
          ecommerce: ecommerceParams,
        });
      } else {
        window.dataLayer.push({
          event: eventName,
          ecommerce: { ...normalizedParams },
        });
      }
    } else {
      window.dataLayer.push({
        event: eventName,
        ...normalizedParams,
      });
    }
  }

  const moosendWasTracked = trackMoosendEvent(eventName, {
    ...moosendParams,
    transaction_id: transactionId,
    moosendItemImage,
    moosendItemUrl,
  });

  // Mark purchase as sent after dispatch so the same transaction is never double-counted
  // in the current session, even if multiple channels are active.
  if (eventName === 'purchase' && transactionId) {
    markTransactionAsSent(transactionId);
  }

  return moosendWasTracked;
};

/**
 * Trigger a page view across Moosend and the GTM dataLayer.
 *
 * The direct gtag('event', 'page_view') call and the Facebook Pixel PageView
 * have been removed; page views are pushed to window.dataLayer without send_to
 * so GTM can route them centrally.
 */
export const trackPageView = url => {
  const isEnabled = process.env.REACT_APP_ENABLE_MARKETING_TRACKING === 'true';
  if (!isEnabled) return;

  // DataLayer (for GTM custom tags) — no send_to; GTM routes events centrally.
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'page_view',
      page_path: url,
      google_business_vertical: RETAIL_VERTICAL,
    });
  }

  trackMoosendPageView();
};

/**
 * Helper to format listing data for analytics events.
 */
export const formatListingForAnalytics = (listing, config) => {
  if (!listing || !listing.id) return null;

  const { title, price, publicData } = listing.attributes;
  const priceNumber = price ? convertMoneyToNumber(price) : 0;

  const listingId = listing.id.uuid;
  return {
    item_id: listingId,
    id: listingId,
    item_name: title,
    price: priceNumber,
    item_category: publicData?.category,
    item_category2: publicData?.subCategory,
    item_brand: publicData?.designer,
    google_business_vertical: RETAIL_VERTICAL,
    quantity: 1,
  };
};

export const moosendListingImageUrl = listing => {
  const imageVariants = listing?.images?.[0]?.attributes?.variants;
  if (!imageVariants) return null;

  const image = Object.values(imageVariants).find(variant => variant?.url);
  return image?.url || null;
};
